import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/auth/AuthContext';
import { supabase } from '../lib/supabaseClient';

const Signup: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    inviteCode: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validateForm = () => {
    // Reset errors
    setError('');
    
    // Check for empty fields
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    if (!formData.password) {
      setError('Password is required');
      return false;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    if (!acceptTerms) {
      setError('You must accept the Terms of Service and Privacy Policy');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Form validation
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      let teamId: string | null = null;
      let userRole = 'customer';

      // Validate invite code if provided
      if (formData.inviteCode.trim()) {
        const { data: inviteData, error: inviteError } = await supabase
          .from('team_invites')
          .select('id, team_id, used, expires_at')
          .eq('invite_code', formData.inviteCode.trim())
          .single();

        if (inviteError || !inviteData) {
          throw new Error('Invalid invite code. Please check and try again.');
        }

        if (inviteData.used) {
          throw new Error('This invite code has already been used.');
        }

        if (new Date(inviteData.expires_at) < new Date()) {
          throw new Error('This invite code has expired.');
        }

        // Valid invite code - user will be crew
        teamId = inviteData.team_id;
        userRole = 'crew';
      }

      // Create user metadata
      const userMetadata = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        name: `${formData.firstName} ${formData.lastName}`,
        full_name: `${formData.firstName} ${formData.lastName}`,
        phone_number: formData.phoneNumber
      };

      // Sign up with email, password, and metadata
      const { error: signUpError, success, user } = await signUp(
        formData.email,
        formData.password,
        userMetadata
      );

      if (signUpError) {
        throw new Error(signUpError.message || 'Failed to create account');
      }

      if (success && user) {
        // Update user role if crew
        if (userRole === 'crew' && user.id) {
          const { error: roleError } = await supabase
            .from('user_profiles')
            .update({ role: 'crew' })
            .eq('id', user.id);

          if (roleError) {
            console.error('Failed to update user role:', roleError);
          }

          // Link user to team and mark invite as used
          if (teamId) {
            const { error: teamError } = await supabase
              .from('team')
              .update({ user_id: user.id })
              .eq('id', teamId);

            if (teamError) {
              console.error('Failed to link user to team:', teamError);
            }

            // Mark invite code as used
            await supabase
              .from('team_invites')
              .update({ used: true, used_by: user.id })
              .eq('invite_code', formData.inviteCode.trim());
          }
        }

        // Redirect to email verification page immediately
        navigate('/verify-email', {
          state: { email: formData.email },
          replace: true
        });
      } else {
        setError('Signup failed. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-[#3A5E63] font-serif">Create an Account</h2>
          <p className="mt-2 text-sm text-[#344854]/70">
            Sign up to access exclusive features and services
          </p>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}
        
        {message && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md text-sm">
            {message}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-[#344854]">
                First Name
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border border-[#B2D8C5] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                placeholder="John"
              />
            </div>
            
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-[#344854]">
                Last Name
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border border-[#B2D8C5] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                placeholder="Doe"
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[#344854]">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border border-[#B2D8C5] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-[#344854]">
              Phone Number <span className="text-[#344854]/50 text-xs">(optional)</span>
            </label>
            <input
              id="phoneNumber"
              name="phoneNumber"
              type="tel"
              autoComplete="tel"
              value={formData.phoneNumber}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-[#B2D8C5] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
              placeholder="(555) 123-4567"
            />
          </div>

          <div>
            <label htmlFor="inviteCode" className="block text-sm font-medium text-[#344854]">
              Team Invite Code <span className="text-[#344854]/50 text-xs">(optional - for staff only)</span>
            </label>
            <input
              id="inviteCode"
              name="inviteCode"
              type="text"
              value={formData.inviteCode}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-[#B2D8C5] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
              placeholder="CREW-ABC123"
            />
            <p className="mt-1 text-xs text-[#344854]/70">
              If you have a team invite code, enter it here to join as a crew member
            </p>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-[#344854]">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border border-[#B2D8C5] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
              placeholder="••••••••"
            />
            <p className="mt-1 text-xs text-[#344854]/70">
              Password must be at least 6 characters
            </p>
          </div>
          
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#344854]">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border border-[#B2D8C5] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
              placeholder="••••••••"
            />
          </div>
          
          <div className="flex items-center">
            <input
              id="terms"
              name="terms"
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              required
              className="h-4 w-4 border-[#B2D8C5] rounded text-[#3A5E63] focus:ring-[#3A5E63]"
            />
            <label htmlFor="terms" className="ml-2 block text-sm text-[#344854]">
              I agree to the <Link to="/terms-of-service" target="_blank" className="text-[#3A5E63] hover:underline">Terms of Service</Link> and <Link to="/privacy-policy" target="_blank" className="text-[#3A5E63] hover:underline">Privacy Policy</Link>
            </label>
          </div>
          
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#3A5E63] hover:bg-[#2d4a4d] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3A5E63] transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating account...
                </span>
              ) : (
                'Sign up'
              )}
            </button>
          </div>
        </form>
        
        <div className="mt-6 text-center">
          <p className="text-sm text-[#344854]/70">
            Already have an account?{' '}
            <Link to="/login" className="text-[#3A5E63] hover:text-[#2d4a4d] font-medium">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup; 